import { Argument, Command } from 'commander';
import { PROJECT_CONFIG_NAME } from '../utils/config';
import * as v from 'valibot';
import { Server } from '@modelcontextprotocol/sdk/server/index.js';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import {
	CallToolRequestSchema,
	CallToolResult,
	ListResourcesRequestSchema,
	ListToolsRequestSchema,
	ReadResourceRequestSchema,
	ToolSchema
} from '@modelcontextprotocol/sdk/types.js';
import pkg from '../../package.json';
import { DEFAULT_REPOS, NGM_DEV_CLI_NAME } from '../constants';
import * as registry from '../utils/registry-providers/internal';
import { z } from 'zod';
import { INFO } from '../utils/ascii';
import { getAngularVersion } from '../utils/angular-version';

// Schema definitions
const schema = v.object({
	verbose: v.boolean(),
	angularVersion: v.optional(v.string())
});

type Options = v.InferInput<typeof schema>;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ToolInputSchema = ToolSchema.shape.inputSchema;
type ToolInput = z.infer<typeof ToolInputSchema>;
const GetAllBlockNamesToolArgsSchema = z.object({});
const SetupAngularMaterialProjectToolArgsSchema = z.object({});

export const mcp = new Command('mcp')
	.description(
		`Run an MCP server to generate Angular Material blocks.

#### Usage

Add the following to your MCP config file:

\`\`\`json
{
	"mcpServers": {
		"ngm-dev-blocks": {
			"command": "npx",
			"args": [
				"-y",
				"@ngm-dev/cli",
				"mcp",
				"full/path/to/angular/project" // mostly full path of current working directory
			]
		}
	}
}

\`\`\`

`
	)
	.addArgument(
		new Argument(
			'<working-dir>',
			`The directory to root of the project, ideally where ${PROJECT_CONFIG_NAME} is present`
		).argRequired()
	)
	.option('-v, --verbose', 'Verbose output', false)
	.option(
		'--angular-version [version]',
		'Angular version to be used for the block generation. If not provided, it will be inferred from the project. Supported versions are: 19, 20, 21.'
	)
	.action(async (workingDir, opts) => {
		const options = v.parse(schema, opts);
		await _mcp(workingDir, options);
	});

async function _mcp(workingDir: string, options: Options) {
	const verbose = (msg: string) => {
		if (options.verbose) {
			console.info(`${INFO} ${msg}`);
		}
	};
	const angularVersion = getAngularVersion({
		cwd: workingDir,
		angularVersion: options.angularVersion,
		verbose
	});
	let errorMessage = '';

	const resolvedRepos: registry.RegistryProviderState[] = (
		await registry.forEachPathGetProviderState(DEFAULT_REPOS, { noCache: true })
	).match(
		(val) => val,
		({ repo }) => {
			errorMessage = `Failed to get info for ${repo}`;
			return [];
		}
	);

	if (errorMessage) {
		return {
			content: [{ type: 'text' as const, text: errorMessage }]
		};
	}

	// verbose(`Resolved ${color.cyan(repoPaths.join(', '))}`);

	// verbose(`Fetching blocks from ${color.cyan(repoPaths.join(', '))}`);

	const manifests = (
		await registry.fetchManifests({
			repos: resolvedRepos,
			version: angularVersion
		})
	).match(
		(v) => v,
		({ repo }) => {
			errorMessage = `Failed fetching blocks from ${repo}`;
			return [];
		}
	);

	if (errorMessage) {
		return {
			content: [{ type: 'text' as const, text: errorMessage }]
		};
	}

	const blocksMap = registry.getRemoteBlocks(manifests);

	const validBlockNames = Array.from(blocksMap.entries())
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		.filter(([_, value]) => value.list)
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		.map(([_, value]) => `"${value.category}/${value.name}"`)
		.join(', ');

	// if (!options.verbose && !options.silent)
	// 	loading.stop(`Retrieved blocks from ${color.cyan(repoPaths.join(', '))}`);

	// verbose(`Retrieved blocks from ${color.cyan(repoPaths.join(', '))}`);

	// Create server instance
	const server = new Server(
		{
			name: 'angular-block-generating-service',
			version: pkg.version
		},
		{
			capabilities: {
				resources: {},
				tools: {}
			}
		}
	);

	// Dynamic tool schema
	const GenerateAngularMaterialBlockToolArgsSchema = z.object({
		blockNames: z
			.array(z.string())
			.nonempty()
			.describe(
				`Names of the blocks to be added in the project, e.g.: "free-authentication/login-with-email-password".
You can try to understand the block name from user's prompt. Or you can ask the user to provide the block name.
But, it must be provided. All the possible block names are: ${validBlockNames}.`
			)
	});

	type GenerateAngularMaterialBlockToolArgs = z.infer<
		typeof GenerateAngularMaterialBlockToolArgsSchema
	>;

	// Tools implementation
	async function generateAngularMaterialBlock(
		args: GenerateAngularMaterialBlockToolArgs
	): Promise<CallToolResult> {
		const { blockNames } = args;
		const result: CallToolResult = {
			content: []
		};
		for (const blockName of blockNames) {
			result.content.push({
				type: 'text',
				text: `Run \`npx @ngm-dev/cli add ${blockName} --skip-asking-for-dependencies\` to add the block to the project`
			});
		}
		return result;
	}

	// List available resources: blocknames and blockfiles (WIP)
	server.setRequestHandler(ListResourcesRequestSchema, async () => {
		return {
			resources: [
				{
					uri: 'blocknames:///all',
					name: 'All available block names',
					mimeType: 'application/json'
				}
			]
		};
	});

	// Handle blocknames resource
	server.setRequestHandler(ReadResourceRequestSchema, async (request) => {
		const { uri } = request.params;
		switch (uri) {
			case 'blocknames:///all':
				return {
					contents: [
						{
							uri,
							mimeType: 'application/json',
							text: validBlockNames
						}
					]
				};
			default:
				return {
					contents: []
				};
		}
	});

	// Tool handlers
	server.setRequestHandler(ListToolsRequestSchema, async () => {
		return {
			tools: [
				{
					name: 'setup-angular-material-blocks',
					description: `Run this tool only if \`${PROJECT_CONFIG_NAME}\` is not present in current working project.

Run this tool to setup the project to be ready to use Angular Material blocks. This tool will create the project structure and install the dependencies.
This toll will run \`npx ${NGM_DEV_CLI_NAME} init\` command, which will do following:

- Setup \`${PROJECT_CONFIG_NAME}\` file in your project. This file contains the configuration for @ngm-dev/cli.
- Creates \`postcssrc.json\`
- Installs \`tailwindcss\` and \`@angular/material\`
- Adds needed styles in \`angular.json\`
- Configures Material Symbols

If for some reason CLI fails to complete above steps, you can ask user to follow manual setup, from [Manual Setup page](https://ui.angular-material.dev/docs/manual-setup).`,
					inputSchema: z.toJSONSchema(SetupAngularMaterialProjectToolArgsSchema) as ToolInput
				},
				{
					name: 'generate-angular-material-block',
					description: `Generate an angular block using @angular/material and Tailwind CSS from [Angular Material Blocks](https://ui.angular-material.dev).
This tool will NOT GENERATE THE FILES. It will only provide the commands to create the files.

Example response:

\`\`\`json
[
"Run \`npx @ngm-dev/cli add free-dialogs/dialog-1 --skip-asking-for-dependencies\` to add the block to the project",
"Run \`npx @ngm-dev/cli add free-dialogs/dialog-2 --skip-asking-for-dependencies\` to add the block to the project",
]
\`\`\`

For above response, you need to run all the commands in the array, one by one, without failing.

If you see  \`unauthenticated\` or \`unauthorized\` errors in response, you can ask user to get access from [Angular Material Blocks All Access Pass](https://ui.angular-material.dev/home#pricing) and then authenticate or authorize themselves using \`npx ${NGM_DEV_CLI_NAME} auth\` command.`,
					inputSchema: z.toJSONSchema(GenerateAngularMaterialBlockToolArgsSchema) as ToolInput
				},
				{
					name: 'get-all-block-names',
					description: `Get all the available block names from [Angular Material Blocks](https://ui.angular-material.dev).`,
					inputSchema: z.toJSONSchema(GetAllBlockNamesToolArgsSchema) as ToolInput
				}
			]
		};
	});

	server.setRequestHandler(CallToolRequestSchema, async (request) => {
		try {
			const { name, arguments: args } = request.params;

			switch (name) {
				case 'generate-angular-material-block': {
					const parsed = GenerateAngularMaterialBlockToolArgsSchema.safeParse(args);
					if (!parsed.success) {
						throw new Error(
							`Invalid arguments for generate-angular-material-block: ${parsed.error}`
						);
					}
					return await generateAngularMaterialBlock(parsed.data);
				}
				case 'get-all-block-names': {
					// const parsed = GetAllBlockNamesToolArgsSchema.safeParse(args);
					// if (!parsed.success) {
					// 	throw new Error(
					// 		`Invalid arguments for get-all-block-names: ${parsed.error}`
					// 	);
					// }
					return {
						content: [
							{
								type: 'text',
								text: JSON.stringify(validBlockNames, null, 2)
							}
						]
					};
				}
				case 'setup-angular-material-blocks': {
					// const parsed = SetupAngularMaterialProjectToolArgsSchema.safeParse(args);
					// if (!parsed.success) {
					// 	throw new Error(`Invalid arguments for setup-angular-material-blocks: ${parsed.error}`);
					// }
					return {
						content: [
							{
								type: 'text',
								text: `Ask user to run \`npx ${NGM_DEV_CLI_NAME} init\` command to setup the project to be ready to use Angular Material blocks.`
							}
						]
					};
				}
				default:
					throw new Error(`Unknown tool: ${name}`);
			}
		} catch (error) {
			return {
				content: [{ type: 'text' as const, text: `Error parsing arguments: ${error}` }]
			};
		}
	});

	async function main() {
		const transport = new StdioServerTransport();
		await server.connect(transport);
		console.error('Angular block generating service MCP Server running on stdio');
	}

	main().catch((error) => {
		console.error('Fatal error in main():', error);
		process.exit(1);
	});
}
